import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Switch,
  MenuItem,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Close,
  AddCircleOutlineOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { motion, AnimatePresence } from "framer-motion";
import { useTemplates } from "./hooks/useTemplate";
import type { IThumbnailTemplate } from "../../../types/thumbnail-template";
import CustomSelect from "../../../components/common/Select";
import CustomTextField from "../../../components/common/Textfield";
import { toast } from "react-toastify";
import ColorPicker from "../../../components/ColorPicker";
import { useSearchParams } from "react-router-dom";

type CategoryType = "all" | IThumbnailTemplate["category"];

const CATEGORIES: { label: string; value: CategoryType }[] = [
  { label: "All", value: "all" },
  { label: "Funeral", value: "funeral" },
  { label: "Church", value: "church" },
  { label: "Conference", value: "conference" },
  { label: "Funeral Home", value: "funeral_home" },
];

const TemplateManager: React.FC = () => {
  const [searchParams] = useSearchParams();
  const thumbnail_type =
    (searchParams.get("thumbnail_type") as "youtube_thumbnail" | "aone") ||
    "youtube_thumbnail";

  const [selectedCategory, setSelectedCategory] = useState<CategoryType>("all");

  // Pass selectedCategory to the hook — triggers a new API call on tab change
  const {
    templates,
    loading,
    error,
    addTemplate,
    updateTemplate,
    deleteTemplate,
  } = useTemplates(selectedCategory);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<IThumbnailTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "funeral" as IThumbnailTemplate["category"],
    templateImageUrl: "",
    backgroundImageUrl: "",
    isActive: true,
    them_color: "#000000",
  });
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);

  const handleOpenDialog = (template?: IThumbnailTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        category: template.category,
        templateImageUrl: template.foreground_image,
        backgroundImageUrl: template.background_image,
        isActive: template.is_active,
        them_color: template.them_color || "#000000",
      });
      setTemplateFile(null);
      setBackgroundFile(null);
    } else {
      setEditingTemplate(null);
      setFormData({
        name: "",
        category: "funeral",
        templateImageUrl: "",
        backgroundImageUrl: "",
        isActive: true,
        them_color: "#000000",
      });
      setTemplateFile(null);
      setBackgroundFile(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setShowColorPicker(false);
    if (formData.templateImageUrl.startsWith("blob:")) {
      URL.revokeObjectURL(formData.templateImageUrl);
    }
    if (formData.backgroundImageUrl.startsWith("blob:")) {
      URL.revokeObjectURL(formData.backgroundImageUrl);
    }
  };

  const handleFileUpload = (file: File, type: "template" | "background") => {
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      [type === "template" ? "templateImageUrl" : "backgroundImageUrl"]:
        previewUrl,
    }));
    if (type === "template") setTemplateFile(file);
    else setBackgroundFile(file);
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      const currentThumbnailType =
        (searchParams.get("thumbnail_type") as "youtube_thumbnail" | "aone") ||
        "youtube_thumbnail";

      const form = new FormData();
      form.append("name", formData.name);
      form.append("category", formData.category);
      form.append("is_active", formData.isActive.toString());
      form.append("them_color", formData.them_color);
      form.append("thumbnail_type", currentThumbnailType);

      if (templateFile) form.append("foreground_image", templateFile);
      if (backgroundFile) form.append("background_image", backgroundFile);

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, form);
        toast.success("Template updated successfully!");
      } else {
        await addTemplate(form);
        toast.success("Template added successfully!");
      }

      if (formData.templateImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.templateImageUrl);
      }
      if (formData.backgroundImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.backgroundImageUrl);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      console.error("Error saving template:", error);
      const errors = (
        error as { response?: { data?: Record<string, unknown> } }
      )?.response?.data || {
        message: "Failed to save template.",
      };
      const errorMsg = Object.values(errors).flat().join(" ");
      setErrorMessage(errorMsg);
      toast.error(`Failed to save template: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteTemplate(id);
        toast.success("Template deleted successfully!");
      } catch (error: unknown) {
        console.error("Error deleting template:", error);
        const errorResponse = (
          error as {
            response?: { data?: { detail?: string } | Record<string, unknown> };
            message?: string;
          }
        )?.response;
        const userFacingMessage =
          (typeof errorResponse?.data === "object" &&
            (errorResponse.data as { detail?: string }).detail) ||
          (errorResponse?.data &&
            typeof errorResponse.data === "object" &&
            Object.values(errorResponse.data).flat().join(" ")) ||
          (error as { message?: string })?.message ||
          "Failed to delete template. Please try again.";
        setErrorMessage(userFacingMessage);
        toast.error(`Error: ${userFacingMessage}`);
      }
    }
  };

  const getCategoryColor = (category: IThumbnailTemplate["category"]) => {
    switch (category) {
      case "funeral":
        return "primary";
      case "church":
        return "secondary";
      case "funeral_home":
        return "success";
      default:
        return "default";
    }
  };

  useEffect(() => {
    return () => {
      if (formData.templateImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.templateImageUrl);
      }
      if (formData.backgroundImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.backgroundImageUrl);
      }
    };
  }, [formData.templateImageUrl, formData.backgroundImageUrl]);

  return (
    <Box>
      <Snackbar
        open={!!error || !!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {error || errorMessage}
        </Alert>
      </Snackbar>

      {/* Header row: Tabs on the left, Add button on the right */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={1}
      >
        <Tabs
          value={selectedCategory}
          onChange={(_e, newVal) => setSelectedCategory(newVal as CategoryType)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "capitalize",
              minHeight: 40,
              py: 0.5,
            },
          }}
        >
          {CATEGORIES.map(({ label, value }) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlined />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: colors.primary[400],
            color: colors.primary[900],
            flexShrink: 0,
          }}
        >
          {thumbnail_type === "youtube_thumbnail" ? "Add Thumbnail" : "Add A1"}
        </Button>
      </Box>

      {/* Template Grid */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <AnimatePresence mode="wait">
            {templates.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px"
                >
                  <Typography variant="h5" color="text.secondary">
                    No templates available
                    {selectedCategory !== "all"
                      ? ` for "${CATEGORIES.find((c) => c.value === selectedCategory)?.label}"`
                      : ""}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              templates.map((template) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={template.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        boxShadow: 0,
                        "&:hover": { boxShadow: 3 },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="170"
                        image={template.foreground_image}
                        alt={template.name}
                        sx={{ objectFit: "fill" }}
                      />
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="start"
                          mb={1}
                        >
                          <Typography variant="h6">{template.name}</Typography>
                          <Chip
                            label={template.category.replace("_", " ")}
                            color={getCategoryColor(template.category)}
                            size="small"
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={2}
                        >
                          Created:{" "}
                          {new Date(template.created_at).toLocaleDateString()}
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(template)}
                            sx={{
                              flex: 1,
                              backgroundColor: colors.primary[800],
                              color: colors.primary[100],
                            }}
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(template.id)}
                            sx={{
                              flex: 1,
                              backgroundColor: colors.primary[400],
                              color: colors.primary[900],
                            }}
                            size="small"
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))
            )}
          </AnimatePresence>
        </Grid>
      )}

      {/* Template Management Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "700px",
            maxWidth: "90vw",
            maxHeight: "85vh",
            margin: "auto",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <DialogTitle>
            {editingTemplate ? "Edit Template" : "Add New Template"}
          </DialogTitle>
          <IconButton onClick={handleCloseDialog} sx={{ mr: 1 }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                fullWidth
                label="Template Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <CustomSelect
                  label="Category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target
                        .value as IThumbnailTemplate["category"],
                    }))
                  }
                >
                  <MenuItem value="funeral">Funeral</MenuItem>
                  <MenuItem value="church">Church</MenuItem>
                  <MenuItem value="funeral_home">Funeral Home</MenuItem>
                  <MenuItem value="conference">Conference</MenuItem>
                </CustomSelect>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Template Image (Foreground)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                disabled={uploading}
                sx={{ mb: 1 }}
              >
                Upload Foreground
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, "template");
                  }}
                />
              </Button>
              {formData.templateImageUrl && (
                <Box mt={1} sx={{ borderRadius: 1, overflow: "hidden" }}>
                  <img
                    src={formData.templateImageUrl || "/placeholder.svg"}
                    alt="Template preview"
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Background Image
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                disabled={uploading}
                sx={{ mb: 1 }}
              >
                Upload Background
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, "background");
                  }}
                />
              </Button>
              {formData.backgroundImageUrl && (
                <Box mt={1} sx={{ borderRadius: 1, overflow: "hidden" }}>
                  <img
                    src={formData.backgroundImageUrl || "/placeholder.svg"}
                    alt="Background preview"
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Text Color
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => setShowColorPicker((prev) => !prev)}
                  sx={{
                    borderColor: colors.primary[100],
                    color: colors.primary[100],
                    "&:hover": {
                      borderColor: colors.primary[100],
                      opacity: 0.8,
                    },
                  }}
                >
                  Choose Color
                </Button>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "4px",
                    border: `1px solid ${colors.primary[300]}`,
                    backgroundColor: formData.them_color,
                  }}
                />
                <Typography variant="body2">{formData.them_color}</Typography>
              </Box>
              {showColorPicker && (
                <Box sx={{ mt: 2, zIndex: 1300 }}>
                  <ColorPicker
                    label="Text Color"
                    value={formData.them_color}
                    onChange={(newColor) =>
                      setFormData((prev) => ({
                        ...prev,
                        them_color: newColor,
                      }))
                    }
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          {uploading && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2">Uploading...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.name ||
              !formData.category ||
              (!templateFile &&
                !formData.templateImageUrl &&
                !editingTemplate) ||
              (!backgroundFile &&
                !formData.backgroundImageUrl &&
                !editingTemplate) ||
              uploading
            }
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
            }}
          >
            {editingTemplate ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManager;
